import { getTranslatedText, ALL_MODULES } from '@nile/components';
import {
  getApiMethodAndKeyNames,
  apiResolver,
  RMMClient,
  AccountClient,
  GET_CUSTOM_FIELD_BY_NAME,
  GET_TECHNICIANS_BY_CRITERIA
} from '@nile/graphql';
import {
  GET_WORKSTATION_LIST,
  GET_SOFTWARENAME_LIST,
  GET_WORKSTATION_ASSET_CLASS_LIST
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { GET_ASSET_CLASS_LIST_V2 } from '@nile/banyan/src/Graphql/Query/asset';
import { GET_PATCH_ALL_CATEGORY_SEVERITY_META } from '@nile/banyan/src/Graphql/Query/patch';
import {
  assetStatusOptions,
  platformCategoryOptions,
  assetClassName,
  assetSmartStatusStatusOptions,
  rebootOptions
} from '@nile/rmm/src/modules/asset-management/components/constants/asset-rule-builder';
import { GET_NETWORK_MONITORING_ASSET_CLASSES } from '@nile/rmm/src/snmp/SnmpQuery';

function getQuery(item) {
  switch (item.name) {
    case 'name':
    case 'asset.name':
      return GET_WORKSTATION_LIST;
    case 'assetSoftware.name':
    case 'assetSoftwareInventory.software.name':
    case 'filteredSoftware.softwareName':
    case 'filteredSoftware.paddedSoftwareVersion':
      return GET_SOFTWARENAME_LIST;
    case 'user.name':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      break;
  }
}

const getClient = (item) => {
  switch (item.name) {
    case 'user.name':
      return AccountClient;
    default:
      return RMMClient;
  }
};

/**
 * @description This method gets triggered on selecting a particular alert attribute
 * Responsible to return the accessKey based on the selected alert attribute
 * @return {string} - accessKey
 */
function getNameKey(item) {
  switch (item) {
    case 'name':
    case 'asset.name':
      return 'getWorkStationList';
    case 'assetSoftware.name':
    case 'assetSoftwareInventory.software.name':
    case 'filteredSoftware.softwareName':
    case 'filteredSoftware.paddedSoftwareVersion':
      return 'getAssetSoftwareNamesList';
    case 'user.name':
      return 'getTechnicianList';
    default:
      break;
  }
}

function getPropValues(
  item,
  operator,
  allOperators,
  intl,
  fromReportBuilder = false,
  currentModule
) {
  if (item?.name) {
    if (item.isCustom) {
      if (item.filterType === 'select') {
        return AccountClient.query({
          query: GET_CUSTOM_FIELD_BY_NAME,
          variables: {
            columnName: item.name,
            module: ALL_MODULES.asset,
            assetClassEntityName: 'WorkStation'
          }
        });
      }
      if (operator) {
        if (
          allOperators?.getRuleBuilderOperators?.operator_placeholders?.[
            operator
          ]
        ) {
          return allOperators?.getRuleBuilderOperators?.operator_placeholders?.[
            operator
          ].map((el) => {
            return {
              ...el,
              label: getTranslatedText(intl, el.key),
              value: getTranslatedText(intl, el.key),
              name: el.key
            };
          });
        } else {
          return [];
        }
      }
    }

    let query = apiResolver.Asset[item.name]?.api;
    let client = apiResolver.Asset[item.name]?.client;
    let variables = {};

    if (fromReportBuilder) {
      switch (item.name) {
        case 'assetClass.name':
          if (currentModule === 'allassets') {
            query = GET_ASSET_CLASS_LIST_V2;
            client = RMMClient;
          } else if (currentModule === 'nmasset') {
            query = GET_NETWORK_MONITORING_ASSET_CLASSES;
            client = RMMClient;
          } else {
            query = GET_WORKSTATION_ASSET_CLASS_LIST;
            client = RMMClient;
          }
          break;
        default:
          break;
      }
    }

    switch (item.name) {
      case 'workStation.client.name':
      case 'asset.client.name':
      case 'client.name':
        variables = {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        };
        break;
      case 'owner.name':
        variables = {
          listInfo: {}
        };
        break;
      case 'assetSoftwareInventory.software.name':
      case 'assetSoftware.name':
        variables = {
          listInfo: {
            page: 1,
            pageSize: 300
          }
        };
        break;
      case 'assetClass.name':
      case 'asset.assetClass.name':
      case 'workStation.assetClass.name':
        if (currentModule === 'nmasset') {
          variables = {};
        } else {
          variables = {
            moduleType: ['UN_MONITORED_ASSET', 'ASSET']
          };
        }
        break;
      default:
        variables = {};
        break;
    }

    if (query && client) {
      return client.query({
        query: query,
        variables
      });
    }
    if (operator) {
      return allOperators?.getRuleBuilderOperators?.operator_placeholders?.[
        operator
      ].map((el) => {
        return {
          ...el,
          label: getTranslatedText(intl, el.key),
          value: getTranslatedText(intl, el.key),
          name: el.key
        };
      });
    }
    return query && client
      ? client.query({
          query: query,
          variables
        })
      : { client: getClient(item), query: getQuery(item) };
  }
  return {};
}

const fetchValuesFromNestedProperties = (data) => {
  const { allValues, method, nestedKey, accessKey } = data;
  return allValues.data
    ? allValues.data[method][nestedKey].map((el) => {
        return {
          name: el[accessKey],
          value: el[accessKey],
          label: el[accessKey]
        };
      })
    : [];
};

async function onAssetViewAttributeSelect(params) {
  const {
    item: field,
    operator,
    fields,
    allOperators,
    intl,
    fromReportBuilder,
    currentModule
  } = params;

  const allFields = fields;
  const selectedField = allFields.find((el) => el.name === field);

  const getPropsFields = [
    'name',
    'user.name',
    'asset.name',
    'assetSoftware.name',
    'assetSoftwareInventory.software.name'
  ];

  const assetPatchDetailAttr = ['patchDetail.category', 'patchDetail.severity'];

  if (
    selectedField?.name === 'platformCategory' ||
    selectedField?.name === 'workStation.platformCategory'
  ) {
    return platformCategoryOptions;
  }

  if (selectedField?.name === 'monitoringStatus') {
    return [
      {
        label: 'Supported',
        name: 'SUPPORTED',
        value: 'SUPPORTED'
      },
      {
        label: 'Not Supported',
        name: 'NOT_SUPPORTED',
        value: 'NOT_SUPPORTED'
      }
    ];
  }

  if (selectedField?.name === 'status') {
    return assetStatusOptions;
  }

  if (selectedField?.name === 'rebootNeeded') {
    return rebootOptions;
  }

  if (getPropsFields.includes(selectedField?.name)) {
    return getPropValues(selectedField, null, allOperators);
  }
  if (selectedField?.name === 'smartStatus') {
    return assetSmartStatusStatusOptions;
  }
  if (selectedField?.name === 'installationStatus') {
    return [
      {
        label: 'New or Missing',
        name: 'NewOrMissing',
        value: 'NewOrMissing'
      },
      {
        label: 'Reboot Required',
        name: 'RebootRequired',
        value: 'RebootRequired'
      },
      { label: 'Installed', name: 'Installed', value: 'Installed' },
      { label: 'Failed', name: 'Failed', value: 'Failed' }
    ];
  }

  let allValues = await getPropValues(
    selectedField,
    operator,
    allOperators,
    intl,
    fromReportBuilder,
    currentModule
  );

  if (allValues && allValues.client && allValues.query) {
    return allValues;
  }

  if (assetPatchDetailAttr.includes(selectedField?.name)) {
    allValues = await getPropValues(selectedField, null, allOperators);
  }

  let res = [];
  const apiMethodAndKeyNames = getApiMethodAndKeyNames('Asset');

  if (selectedField?.name) {
    let method = apiMethodAndKeyNames[selectedField.name]?.method;
    let key = apiMethodAndKeyNames[selectedField.name]?.key;

    if (fromReportBuilder) {
      switch (selectedField?.name) {
        case 'assetClass.name':
          if (currentModule === 'allassets') {
            method = 'getAssetClassListV2';
            key = 'name';
          } else if (currentModule === 'nmasset') {
            method = 'getNetworkMonitoringAssetClasses';
            key = 'name';
          } else {
            method = 'getWorkStationAssetClassList';
            key = 'name';
          }
          break;
        default:
          break;
      }
    }

    let nestedData =
      apiMethodAndKeyNames[selectedField.name]?.nestedData || null;

    if (selectedField.nestedTo) {
      let nestedProp = apiMethodAndKeyNames[selectedField.name]?.nestedKey;
      allValues.data[method].forEach((el) => {
        res = res.concat(
          el[[nestedProp]].map((c) => {
            return {
              name: c[key],
              value: c[key],
              label: c[key]
            };
          })
        );
      });
    } else {
      if (selectedField.isCustom) {
        if (operator === 'on') {
          res = allValues;
        } else if (selectedField.filterType === 'select') {
          method = 'getCustomFieldByName';
          res = allValues.data
            ? allValues.data[method].options.map((el) => {
                return {
                  name: el,
                  value: el,
                  label: el
                };
              })
            : [];
        }
      } else {
        if (method === 'getClientList' || method === 'getClientSiteList') {
          const data = {
            allValues,
            method,
            nestedKey: apiMethodAndKeyNames[selectedField.name].nestedKey,
            accessKey: key
          };
          res = fetchValuesFromNestedProperties(data);
        } else if (
          method === 'getPatchAllCategorySeverityMeta' ||
          method === 'getRequesterList'
        ) {
          res = allValues.data
            ? allValues.data[method][nestedData].map((el) => {
                return {
                  name: key ? el[key] : el,
                  value: key ? el[key] : el,
                  label: key ? el[key] : el
                };
              })
            : [];
        } else if (operator === 'on') {
          res = allValues;
        } else {
          res = allValues.data
            ? allValues.data[method].map((el) => {
                return {
                  name: key ? el[key] : el,
                  value: key ? el[key] : el,
                  label: key ? el[key] : el
                };
              })
            : [];
        }
      }
    }
  }

  return res;
}

/**
 * This method is called for autocomplete fields.
 * Usually apis will be called when we choose attributes. But for autocomplete,
 * on typing few characters, api will be called. This is the method.
 * @param {object} client
 * @param {gql} query
 * @param {object} params
 */
const onAssetViewAttributeValueSearch = async (client, query, params) => {
  const paramLength = params.field.split('.').length;
  let attribute;
  if (
    params.field === 'filteredSoftware.softwareName' ||
    params.field === 'filteredSoftware.paddedSoftwareVersion'
  ) {
    attribute = 'name';
  } else {
    attribute =
      paramLength > 1
        ? params?.field?.split('.')[paramLength - 1 || 1]
        : params.field;
  }

  const nameKey = getNameKey(params.field);

  let variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute || 'firstName',
            operator: 'contains',
            value: params.inputValue
          }
        ]
      }
    }
  };

  const res = await client.query({
    query: query,
    variables
  });

  let allOptions = [];

  if (nameKey === 'getAssetSoftwareNamesList') {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].map((el) => {
          return {
            name: `${el}`,
            value: `${el}`,
            label: `${el}`
          };
        })
      : [];
    return allOptions;
  }
  if (nameKey === 'getTechnicianList') {
    allOptions = res?.data?.[nameKey]
      ? res?.data?.[nameKey]?.userList?.map((el) => {
          return {
            name: `${el[attribute]}`,
            value: `${el[attribute]}`,
            label: `${el[attribute]}`
          };
        })
      : [];
  } else if (nameKey === 'getWorkStationList') {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].assets.map((el) => {
          return {
            name: `${el[attribute]}`,
            value: `${el[attribute]}`,
            label: `${el[attribute]}`
          };
        })
      : [];
  } else {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].map((el) => {
          return {
            name: `${el[attribute]}`,
            value: `${el[attribute]}`,
            label: `${el[attribute]}`
          };
        })
      : [];
  }

  return allOptions;
};

export { onAssetViewAttributeSelect, onAssetViewAttributeValueSearch };
